import { IWhatsNewItem } from "api/whatsNew/whatsNew.type";

// REF-6550: presentation-only projection of the raw What's New rows. Features keep their own card;
// improvements and bug fixes published on the same UTC day collapse into a single "Improvements & fixes"
// card. The raw rows stay untouched in Redux — `useFeatureTour` and the tour modal resolve items by id
// from them, so the grouping must never replace the stored feed.
//
// The BE paginates and counts PER ROW, so one day's rows can straddle two pages. Callers that append pages
// re-run this over every row loaded so far; the group then grows in place instead of forming a second card.

export type WhatsNewEntry =
  | { type: "feature"; key: string; item: IWhatsNewItem }
  | {
      type: "release";
      key: string;
      /** Newest first. */
      items: IWhatsNewItem[];
      /** `published_at` of the newest member — the card's date label. */
      publishedAt: string;
    };

const isGroupedKind = (item: IWhatsNewItem) => (item.kind === "improvement" || item.kind === "bug_fix") && item.modal_type !== "how_it_works";

const timestampOf = (item: IWhatsNewItem): number => (item.published_at ? new Date(item.published_at).getTime() : NaN);

// The UTC calendar day of `published_at` — one card per day, whatever `release_key` says. `release_key` is a
// back-office tool (managing / bulk-publishing a release); grouping on it showed two identical
// "Improvements & fixes · <date>" cards when two releases shipped on one day (Okan, REF-6550 follow-up).
// Never `date` — the BE computes it in the DB session timezone, which is not pinned to UTC. An unparseable
// timestamp isolates the row rather than lumping unrelated rows together.
const groupKeyOf = (item: IWhatsNewItem): string => {
  const ts = timestampOf(item);
  if (!Number.isNaN(ts)) return `day:${new Date(ts).toISOString().slice(0, 10)}`;

  return `row:${item.id}`;
};

export const groupWhatsNewItems = (rows: IWhatsNewItem[]): WhatsNewEntry[] => {
  const seen = new Set<string>();
  const entries: { entry: WhatsNewEntry; position: number }[] = [];
  const groups = new Map<string, { items: IWhatsNewItem[]; positions: number[] }>();

  rows.forEach((item, index) => {
    // An appended page can repeat a row when new items were published between requests.
    if (!item || seen.has(item.id)) return;
    seen.add(item.id);

    if (!isGroupedKind(item)) {
      entries.push({ entry: { type: "feature", key: `feature:${item.id}`, item }, position: index });

      return;
    }

    const key = groupKeyOf(item);
    const group = groups.get(key) ?? { items: [], positions: [] };
    group.items.push(item);
    group.positions.push(index);
    groups.set(key, group);
  });

  groups.forEach((group, key) => {
    // Members newest-first; the card sits where its newest member sits in the chronological feed. In a
    // `day:` group every timestamp parses (the key needs it); a `row:` group is a single unparseable row, so
    // there is nothing to compare. The sort is stable, so equal timestamps keep feed order.
    const order = group.items.map((_, i) => i).sort((a, b) => timestampOf(group.items[b]) - timestampOf(group.items[a]));
    const anchor = order[0];

    entries.push({
      entry: { type: "release", key, items: order.map((i) => group.items[i]), publishedAt: group.items[anchor].published_at },
      position: group.positions[anchor],
    });
  });

  return entries.sort((a, b) => a.position - b.position).map(({ entry }) => entry);
};

export default groupWhatsNewItems;
